'use client';

import * as React from 'react';
import { cn } from '@utils';
import { tv } from 'tailwind-variants';
import { Drawer as DrawerPrimitive } from 'vaul';

const Drawer = ({
	shouldScaleBackground = true,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
	<DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Overlay
		ref={ref}
		className={cn('bg-alt-dark-background-alt/95 fixed inset-0 z-50', className)}
		{...props}
	/>
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
		showHandle?: boolean;
		placement?: 'bottom' | 'right' | 'left';
	}
>(({ className, children, placement = 'bottom', showHandle = false, ...props }, ref) => {
	const drawerContent = tv({
		base: ['bg-neutral-white fixed z-50 flex h-auto flex-col'],
		variants: {
			placement: {
				bottom: 'bottom-0 mt-24 min-h-24 w-full rounded-t-sm',
				right: 'right-0 h-full w-full sm:w-80 md:w-105',
				left: 'left-0 h-full w-full sm:w-80 md:w-105',
			},
		},
		defaultVariants: {
			placement: 'bottom',
		},
	});

	return (
		<DrawerPortal>
			<DrawerOverlay />
			<DrawerPrimitive.Content
				ref={ref}
				className={cn(drawerContent({ placement }), className)}
				{...props}
			>
				{showHandle && <div className="bg-neutral-black/30 mx-auto mt-4 h-2 w-20 rounded-full" />}
				{children}
			</DrawerPrimitive.Content>
		</DrawerPortal>
	);
});
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('grid gap-1.5 text-center sm:text-left', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('mt-auto flex flex-col gap-2', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

export {
	Drawer,
	DrawerPortal,
	DrawerOverlay,
	DrawerTrigger,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerFooter,
};
